import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatMoney, formatNumber, humanize } from "../utils/format.ts";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import DistributionChart from "../components/DistributionChart.tsx";
import CountryName from "../components/CountryName.tsx";
import { CpvCodeAutocomplete, useCpvDescriptions } from "../components/CpvCode.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../components/filters/SavedViewsMenu.tsx";
import type { FilterOption, FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import StatusChip from "../components/StatusChip.tsx";
import { MatchedCell, TitleLinkCell } from "../components/table/cells.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import SortableTableHead from "../components/table/SortableTableHead.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useTenant } from "../contexts/TenantContext.tsx";
import { useCountryOptions } from "../hooks/useCountryOptions.ts";
import { useSourceStats } from "../hooks/useSourceStats.ts";
import { useCommaParam, useUrlPaging, useUrlSort, useUrlState } from "../hooks/useUrlState.ts";
import { booleanFilterToParam, parseBooleanFilter, parseFiniteNumber } from "../utils/urlFilters.ts";
import PageHeader from "../components/PageHeader.tsx";

const COLUMNS = [
  { id: "title", label: "Title" },
  { id: "buyer", label: "Buyer" },
  { id: "winner", label: "Winner" },
  { id: "country", label: "Country" },
  { id: "source", label: "Source" },
  { id: "notice_type", label: "Type" },
  { id: "publication_date", label: "Published" },
  { id: "submission_deadline", label: "Deadline" },
  { id: "value", label: "Value" },
  { id: null, label: "Matched" },
];

/** Typed amounts go to the API as typed; incomplete input ("-", ".") is left out. */
function amountParam(value: string | null): string | undefined {
  return value && parseFiniteNumber(value) !== undefined ? value : undefined;
}

const labelled = (values: string[] | undefined, label: (v: string) => string): FilterOption[] =>
  (values ?? []).map((v) => ({ value: v, label: label(v) }));

const MATCHED_LABELS = { yes: "Matched", no: "Unmatched" };

function TendersPage() {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const { currentOrg } = useOrganization();
  const url = useUrlState();
  const { searchParams } = url;

  const { data: filterOptions } = useQuery({
    queryKey: keys.tender.filterOptions(),
    queryFn: () => api.getTenderFilterOptions(),
    staleTime: 30 * 60 * 1000,
  });
  const countryOptions = useCountryOptions(filterOptions?.countries);

  // The picked CPV code's description, for its pill.
  const cpv = searchParams.get("cpv") ?? "";
  const { map: cpvDescriptions } = useCpvDescriptions(cpv ? [cpv] : []);
  const cpvDescription = cpvDescriptions.get(cpv);

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !filterOptions;
    return [
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "notice_type",
        label: "Notice type",
        param: "notice_type",
        pinned: true,
        loading,
        options: labelled(filterOptions?.notice_types, humanize),
      },
      {
        kind: "dateRange",
        key: "published",
        label: "Published",
        pinned: true,
        fromParam: "published_from",
        toParam: "published_to",
      },
      { kind: "text", key: "buyer", label: "Buyer", group: "Parties", param: "buyer", placeholder: "Buyer name" },
      { kind: "text", key: "winner", label: "Winner", group: "Parties", param: "winner", placeholder: "Winner name" },
      {
        kind: "boolean",
        key: "buyer_matched",
        label: "Buyer matched",
        group: "Parties",
        param: "buyer_matched",
        labels: MATCHED_LABELS,
      },
      {
        kind: "boolean",
        key: "winner_matched",
        label: "Winner matched",
        group: "Parties",
        param: "winner_matched",
        labels: MATCHED_LABELS,
      },
      {
        kind: "options",
        key: "source",
        label: "Source",
        group: "Details",
        param: "source",
        loading,
        options: labelled(filterOptions?.sources, (s) => s.toUpperCase()),
      },
      {
        kind: "options",
        key: "procedure_type",
        label: "Procedure",
        group: "Details",
        param: "procedure_type",
        loading,
        options: labelled(filterOptions?.procedure_types, humanize),
      },
      { kind: "boolean", key: "framework", label: "Framework agreement", group: "Details", param: "framework" },
      {
        kind: "custom",
        key: "cpv",
        label: "CPV code",
        group: "Details",
        params: ["cpv"],
        summary: (get) => {
          const code = get("cpv");
          if (!code) return null;
          return code === cpv && cpvDescription ? `${code} · ${cpvDescription}` : code;
        },
        renderEditor: ({ get, set, close }) => (
          <Box sx={{ p: 1.5 }}>
            <CpvCodeAutocomplete
              value={get("cpv")}
              onChange={(code) => {
                set({ cpv: code });
                if (code) close();
              }}
              label=""
              placeholder="Search code or description"
              autoFocus
            />
          </Box>
        ),
      },
      {
        kind: "range",
        key: "value",
        label: "Value",
        group: "Details",
        minParam: "min_value",
        maxParam: "max_value",
        decimal: true,
      },
      {
        kind: "dateRange",
        key: "deadline",
        label: "Submission deadline",
        group: "Dates",
        fromParam: "deadline_from",
        toParam: "deadline_to",
      },
      {
        kind: "dateRange",
        key: "award",
        label: "Award date",
        group: "Dates",
        fromParam: "award_from",
        toParam: "award_to",
      },
    ];
  }, [filterOptions, countryOptions, cpv, cpvDescription]);

  const filters = useFilters(specs, {
    state: url,
    search: { param: "q", placeholder: "Search tenders" },
    pageKey: "tenders",
    scopeId: null,
    remember: true,
  });

  const countries = useCommaParam(searchParams, "country");
  const sources = useCommaParam(searchParams, "source");
  const noticeTypes = useCommaParam(searchParams, "notice_type");
  const procedureTypes = useCommaParam(searchParams, "procedure_type");
  const param = (key: string) => searchParams.get(key) || undefined;
  const yesNo = (key: string) => booleanFilterToParam(parseBooleanFilter(searchParams.get(key)));

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "publication_date", "desc");
  const paging = useUrlPaging(url);

  const queryParams = {
    q: param("q"),
    buyer: param("buyer"),
    winner: param("winner"),
    country: countries.length ? countries : undefined,
    source: sources.length ? sources : undefined,
    notice_type: noticeTypes.length ? noticeTypes : undefined,
    procedure_type: procedureTypes.length ? procedureTypes : undefined,
    is_framework_agreement: yesNo("framework"),
    buyer_matched: yesNo("buyer_matched"),
    winner_matched: yesNo("winner_matched"),
    published_from: param("published_from"),
    published_to: param("published_to"),
    deadline_from: param("deadline_from"),
    deadline_to: param("deadline_to"),
    award_from: param("award_from"),
    award_to: param("award_to"),
    min_value: amountParam(searchParams.get("min_value")),
    max_value: amountParam(searchParams.get("max_value")),
    cpv: cpv || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: paging.rowsPerPage,
    offset: paging.offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.tender.list(queryParams),
    queryFn: () => api.getTenders(queryParams),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const stats = useSourceStats(keys.tender.stats(), api.getTenderStats, (s) => ({
    label: s.source.toUpperCase(),
    count: s.count,
  }));

  return (
    <>
      <PageHeader
        title="Tenders"
        description="Public procurement notices, matched to their buyers and winners"
      />
      <DistributionChart
        title="Tenders per source"
        data={stats.chartData}
        loading={stats.isLoading}
      />
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "tender" : "tenders"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
              />
            }
          />
        }
        footer={data && data.total > 0 && <UrlTablePagination total={data.total} paging={paging} />}
      >
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <TableBody>
            {data?.items.map((t) => {
              const linkCompanyId = t.winner_company_id ?? t.buyer_company_id;
              return (
                <TableRow
                  key={t.id}
                  hover
                  sx={{ cursor: linkCompanyId ? "pointer" : "default" }}
                  onClick={() => {
                    if (linkCompanyId) navigate(tenantPath(`/companies/${linkCompanyId}`));
                  }}
                >
                  <TitleLinkCell title={t.title} href={t.source_url} />
                  <TableCell>{t.buyer_company_name ?? t.buyer_name ?? "-"}</TableCell>
                  <TableCell>{t.winner_company_name ?? t.winner_name ?? "-"}</TableCell>
                  <TableCell>
                    <CountryName code={t.country} />
                  </TableCell>
                  <TableCell>{t.source.toUpperCase()}</TableCell>
                  <TableCell>
                    <StatusChip kind="tender_notice_type" value={t.notice_type} />
                  </TableCell>
                  <TableCell>
                    <FormattedDate value={t.publication_date} />
                  </TableCell>
                  <TableCell>
                    <FormattedDate value={t.submission_deadline} />
                  </TableCell>
                  <TableCell>
                    {formatMoney(t.awarded_value ?? t.estimated_value, t.currency, { decimals: 0 })}
                  </TableCell>
                  <MatchedCell matched={!!(t.winner_company_id || t.buyer_company_id)} />
                </TableRow>
              );
            })}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length}>
                {filters.activeCount > 0 ? "No tenders match your filters" : "No tenders found"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default TendersPage;
